import * as React from 'react';

import cn from 'classnames';

import { IStock } from '@notino/shared/definitions/custom-definitions';

import ErrorBoundary from '@components/ErrorBoundary';
import PriceLabel from '@components/PriceLabel';
import { CornerTag } from '@components/Tags/CornerTag';
import { getSupportedAttributes } from '@components/Tags/model';
import { getVariantName } from '@containers/ProductDetailContainer/ProductDetail/utils';
import { VariantDisplayTypes } from '@containers/ProductListing/model';
import { useTrackingContext } from '@context/tracking/TrackingContext';
import { dataLabelsProp } from '@helpers/utils';
import { typographyClass } from '@styles/common';

import { IVariantProps } from '../../../../model';
import { usePriceWithVoucher } from '../../hooks/usePriceWithVoucher';
import { trackVariantClick } from '../../tracking';
import { VariantLink, PriceLabelCurrency } from '../../variantsStyled';
import { Unavailable } from '../Unavailable';

import {
  PriceLabelWrapper,
  Variant,
  ImgWrapper,
  Img,
  VariantListItem,
  PricePlaceholder,
} from './styled';

export const VariantThumbnail: React.FC<IVariantProps> = ({
  item,
  isSelected,
  displayPrice,
  variantsCount,
  onClick,
}) => {
  const isOnStock = item.stockAvailability.code !== IStock.outOfStock;
  const { getTimeFromInit } = useTrackingContext();
  const supportedAttributes = getSupportedAttributes();
  const displayInRow = variantsCount === 2;

  const price = usePriceWithVoucher(item);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    onClick?.(item, e);
    trackVariantClick({
      timing: getTimeFromInit(),
      type: VariantDisplayTypes.Thumbnail,
      variantName: getVariantName(item) ?? '',
    });
  };

  const cornerTagProps = {
    attributes: item.attributes,
    stockAvailability: item.stockAvailability.code ?? '',
  };

  return (
    <VariantListItem>
      <ErrorBoundary>
        <VariantLink
          onClick={handleClick}
          replace={true}
          to={item.url ?? ''}
          id={`pd-variant-${item.webId}`}
          data-testid={`pd-variant-${item.webId}`}
          className={isSelected ? 'pd-variant-selected' : ''}
          {...dataLabelsProp({
            attributes: item.attributes,
            supportedAttributes,
          })}
        >
          <Variant
            displayInRow={displayInRow}
            isOnStock={isOnStock}
            isSelected={isSelected}
          >
            <CornerTag {...cornerTagProps} />

            <ImgWrapper isOnStock={isOnStock} isSelected={isSelected}>
              <Img
                src={item.media?.mainImage?.src ?? ''}
                alt={item.media?.mainImage?.alt ?? ''}
                sourceKey="thumb"
              />
            </ImgWrapper>

            {displayPrice && isOnStock ? (
              <PriceLabel
                price={{
                  value: price.value ?? 0,
                  currency: price.currency ?? undefined,
                }}
                wrapperComponent={(additionalProps) => (
                  <PriceLabelWrapper
                    data-cypress="variant-thumbnail-price"
                    isSelected={isSelected}
                    isOnStock={isOnStock}
                    className={cn({
                      [typographyClass.labelRegular]: isSelected,
                      [typographyClass.labelRegular400]: !isSelected,
                    })}
                    {...additionalProps}
                  />
                )}
                currencyComponent={PriceLabelCurrency}
              />
            ) : (
              !displayInRow && isOnStock && <PricePlaceholder />
            )}

            {!isOnStock && <Unavailable />}
          </Variant>
        </VariantLink>
      </ErrorBoundary>
    </VariantListItem>
  );
};
